/**
 * Historique / SAV — quantités remboursables par ligne (aligné duplicata + backend posSavRefundQty).
 */

import {
  aggregateRefundedQtyByLineFromPayments,
  lineUnitTtcSafe,
  parseSavPaymentReference,
  roundMoney,
} from './caisseDuplicataRefundDisplay.js';

function soldQtyForLine(lp) {
  return Math.max(0, Math.floor(Number(lp?.quantite) || 0));
}

/** Cumul qty remboursées ; legacy ligneIds sans ligneQuantites → qty vendue sur la ligne. */
export function aggregateRefundedQtyByLine(paiements, produits) {
  const map = { ...aggregateRefundedQtyByLineFromPayments(paiements) };
  const byId = new Map((produits || []).map((lp) => [lp.id, lp]));

  for (const p of paiements || []) {
    if (Number(p.montant) >= -0.001) continue;
    const meta = parseSavPaymentReference(p.reference);
    if (!meta) continue;
    if (meta.ligneQuantites && typeof meta.ligneQuantites === 'object') continue;

    if (meta.type === 'REMBOURSEMENT_COMPLET') {
      for (const lp of byId.values()) {
        const sold = soldQtyForLine(lp);
        if (sold > 0) map[lp.id] = Math.max(map[lp.id] || 0, sold);
      }
      continue;
    }

    if (Array.isArray(meta.ligneIds) && meta.ligneIds.length > 0) {
      for (const lineId of meta.ligneIds) {
        if (map[lineId] > 0) continue;
        const lp = byId.get(lineId);
        const sold = lp ? soldQtyForLine(lp) : 0;
        map[lineId] = (map[lineId] || 0) + (sold > 0 ? sold : 1);
      }
    }
  }

  return map;
}

export function quantiteRetournablePourLigne(lp, refundedByLine) {
  const sold = soldQtyForLine(lp);
  const already = refundedByLine[lp.id] || 0;
  return Math.max(0, sold - already);
}

export function isLineFullyRefunded(lp, refundedByLine) {
  return quantiteRetournablePourLigne(lp, refundedByLine) <= 0;
}

/** @returns {{ vendus, rembourses, conserves, unitTtc, isFullyRefunded, isPartiallyRefunded }} */
export function buildHistoryLineRefundDisplay(lp, refundedByLine) {
  const vendus = soldQtyForLine(lp);
  const rembourses = Math.min(vendus, refundedByLine[lp.id] || 0);
  const conserves = vendus - rembourses;
  return {
    vendus,
    rembourses,
    conserves,
    unitTtc: lineUnitTtcSafe(lp),
    montantRembourseTtc: roundMoney(lineUnitTtcSafe(lp) * rembourses),
    isFullyRefunded: conserves <= 0 && rembourses > 0,
    isPartiallyRefunded: rembourses > 0 && conserves > 0,
    quantiteRetournable: conserves,
  };
}

export function hasAnySavArticleReturnable(produits, paiements) {
  const refundedByLine = aggregateRefundedQtyByLine(paiements, produits);
  return (produits || []).some((lp) => quantiteRetournablePourLigne(lp, refundedByLine) > 0);
}
